import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Copy, CalendarRange, RefreshCw, Plus, X } from 'lucide-react';
import { format, addWeeks, startOfWeek, parseISO, addDays } from 'date-fns';
import { toast } from 'sonner';

export default function CopyDuplicateDialog({ open, onOpenChange, onSuccess, currentDate }) {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('copy');
  const [loading, setLoading] = useState(false);
  
  // Copy form
  const [sourceDate, setSourceDate] = useState(format(currentDate || new Date(), 'yyyy-MM-dd'));
  const [targetDates, setTargetDates] = useState([]);
  const [newTargetDate, setNewTargetDate] = useState('');
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState('all');
  const [overwriteExisting, setOverwriteExisting] = useState(false);

  // Duplicate week form
  const [sourceWeekStart, setSourceWeekStart] = useState(
    format(startOfWeek(currentDate || new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );
  const [targetWeeks, setTargetWeeks] = useState([]);
  const [numWeeksToAdd, setNumWeeksToAdd] = useState(4);

  const addTargetDate = () => {
    if (newTargetDate && !targetDates.includes(newTargetDate)) {
      setTargetDates([...targetDates, newTargetDate].sort());
      setNewTargetDate('');
    }
  };

  const removeTargetDate = (date) => {
    setTargetDates(targetDates.filter(d => d !== date));
  };

  const addTargetWeeks = () => {
    const newWeeks = [];
    const lastWeek = targetWeeks.length > 0 
      ? parseISO(targetWeeks[targetWeeks.length - 1])
      : parseISO(sourceWeekStart);
    
    for (let i = 1; i <= numWeeksToAdd; i++) {
      const weekStart = format(addWeeks(lastWeek, i), 'yyyy-MM-dd');
      if (!targetWeeks.includes(weekStart) && weekStart !== sourceWeekStart) {
        newWeeks.push(weekStart);
      }
    }
    
    setTargetWeeks([...targetWeeks, ...newWeeks].sort());
  };

  const removeTargetWeek = (week) => {
    setTargetWeeks(targetWeeks.filter(w => w !== week));
  };

  const handleCopy = async () => {
    if (targetDates.length === 0) {
      toast.error('Please add at least one target date');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/schedules/copy', {
        source_date: sourceDate,
        target_dates: targetDates,
        schedule_type: scheduleTypeFilter === 'all' ? null : scheduleTypeFilter,
        overwrite_existing: overwriteExisting
      });
      toast.success(`Copied ${res.data.created} schedule entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} due to conflicts`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateWeek = async () => {
    if (targetWeeks.length === 0) {
      toast.error('Please add at least one target week');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/schedules/duplicate-week', {
        source_week_start: sourceWeekStart,
        target_week_starts: targetWeeks,
        schedule_type: scheduleTypeFilter === 'all' ? null : scheduleTypeFilter,
        overwrite_existing: overwriteExisting
      });
      toast.success(`Duplicated week, created ${res.data.created} entries`);
      if (res.data.skipped > 0) {
        toast.info(`Skipped ${res.data.skipped} due to conflicts`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate week');
    } finally {
      setLoading(false);
    }
  };

  const formatWeekRange = (mondayStr) => {
    const monday = parseISO(mondayStr);
    const sunday = addDays(monday, 6);
    return `${format(monday, 'MMM d')} - ${format(sunday, 'MMM d, yyyy')}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5 text-teal-600" />
            Copy & Duplicate Schedules
          </DialogTitle>
          <DialogDescription>
            Copy schedules to other dates or duplicate entire weeks
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="copy">Copy Day</TabsTrigger>
            <TabsTrigger value="week">Duplicate Week</TabsTrigger>
          </TabsList>

          {/* Copy Day Tab */}
          <TabsContent value="copy" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Source Date</Label>
              <Input
                type="date"
                value={sourceDate}
                onChange={(e) => setSourceDate(e.target.value)}
                data-testid="copy-source-date"
              />
              <p className="text-xs text-slate-500">
                All schedules from this date will be copied
              </p>
            </div>

            <div className="space-y-2">
              <Label>Target Dates</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newTargetDate}
                  onChange={(e) => setNewTargetDate(e.target.value)}
                  data-testid="copy-new-target"
                />
                <Button type="button" onClick={addTargetDate} size="icon">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {targetDates.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {targetDates.map(date => (
                    <Badge key={date} variant="secondary" className="pr-1">
                      {date}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1"
                        onClick={() => removeTargetDate(date)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Schedule Type Filter</Label>
              <Select value={scheduleTypeFilter} onValueChange={setScheduleTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schedules</SelectItem>
                  <SelectItem value="PHYSICIAN">Physician Only</SelectItem>
                  <SelectItem value="EMPLOYEE">Employee Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Overwrite Existing</p>
                <p className="text-xs text-slate-500">Replace if dates are occupied</p>
              </div>
              <Switch 
                checked={overwriteExisting}
                onCheckedChange={setOverwriteExisting}
              />
            </div>
          </TabsContent>

          {/* Duplicate Week Tab */}
          <TabsContent value="week" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Source Week (Start Monday)</Label>
              <Input
                type="date"
                value={sourceWeekStart}
                onChange={(e) => setSourceWeekStart(e.target.value)}
                data-testid="dup-source-week"
              />
              <p className="text-xs text-slate-500">
                Week: {formatWeekRange(sourceWeekStart)}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Target Weeks</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={numWeeksToAdd}
                  onChange={(e) => setNumWeeksToAdd(parseInt(e.target.value) || 1)}
                  className="w-20"
                />
                <span className="text-sm text-slate-500">weeks</span>
                <Button type="button" onClick={addTargetWeeks}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              {targetWeeks.length > 0 && (
                <div className="space-y-2 mt-2 max-h-[150px] overflow-y-auto">
                  {targetWeeks.map(week => (
                    <div key={week} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <span className="text-sm">{formatWeekRange(week)}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeTargetWeek(week)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                {targetWeeks.length} week(s) selected
              </p>
            </div>

            <div className="space-y-2">
              <Label>Schedule Type Filter</Label>
              <Select value={scheduleTypeFilter} onValueChange={setScheduleTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Schedules</SelectItem>
                  <SelectItem value="PHYSICIAN">Physician Only</SelectItem>
                  <SelectItem value="EMPLOYEE">Employee Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Overwrite Existing</p>
                <p className="text-xs text-slate-500">Replace if dates are occupied</p>
              </div>
              <Switch 
                checked={overwriteExisting}
                onCheckedChange={setOverwriteExisting}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          
          {activeTab === 'copy' ? (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleCopy}
              disabled={loading || targetDates.length === 0}
              data-testid="copy-schedules-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy to {targetDates.length} Date(s)
            </Button>
          ) : (
            <Button 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleDuplicateWeek}
              disabled={loading || targetWeeks.length === 0}
              data-testid="duplicate-week-btn"
            >
              {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CalendarRange className="h-4 w-4 mr-2" />}
              Duplicate to {targetWeeks.length} Week(s)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
